import React, { useRef, useState, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { X, Download, Copy, Check, Share2 } from 'lucide-react';

/**
 * SharePreviewModal - Generates shareable images of watchlist simulation results
 *
 * Supports both preset mode (multiple ±% scenarios) and custom mode (single scenario with sliders)
 * Includes simulation settings header and optionsleveling.com branding
 */

// Column mapping for different simulation modes
// Labels are shortened versions matching the watchlist table column names
const PRESET_SHARE_COLUMNS = [
  { key: 'Ticker', label: 'Ticker' },
  { key: 'Option Type', label: 'Type' },
  { key: 'Strike', label: 'Strike' },
  { key: 'Expiration', label: 'Exp' },
  { key: 'Days to Target', label: 'Days to Exit' },
  { key: 'Current Underlying', label: 'Current Underlying' },
  { key: 'Underlying Scenario % Change', label: 'Underlying Gain %' },
  { key: 'Simulated Underlying (+)', label: 'Sim Underlying' },
  { key: 'Current Premium', label: 'Premium' },
  { key: 'Simulated Premium (+) % Change', label: 'Premium Gain %' },
  { key: 'Simulated Premium (+)', label: 'Sim Premium' },
];

const CUSTOM_SHARE_COLUMNS = [
  { key: 'Ticker', label: 'Ticker' },
  { key: 'Option Type', label: 'Type' },
  { key: 'Strike', label: 'Strike' },
  { key: 'Expiration', label: 'Exp' },
  { key: 'Days to Gain', label: 'Days to Exit' },
  { key: 'Underlying Price', label: 'Current Underlying' },
  { key: 'Underlying Gain %', label: 'Underlying Gain %' },
  { key: 'Simulated Underlying', label: 'Sim Underlying' },
  { key: 'Current Premium', label: 'Premium' },
  { key: 'Premium % Gain', label: 'Premium Gain %' },
  { key: 'Simulated Premium', label: 'Sim Premium' },
];

function SharePreviewModal({
  isOpen,
  onClose,
  data,
  simulationMode = 'preset',
  daysToTarget,
  underlyingGain,
  columns = null, // Optional column override: [{key, label, render?, className?}] — if provided, uses these instead of preset/custom columns
  settingsHeader = null, // Optional custom settings header JSX — if provided, replaces the default slider visuals
  cellStyleFn = null, // Optional (row, col) => style | null — when returns non-empty style, value class is skipped and style is applied inline
  headerGroups = null, // Optional [{ label, span, className? }] — extra header row above the column-label row
  title = 'Share Results',
}) {
  const captureRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const canShareFiles = useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.canShare) return false;
    try {
      return navigator.canShare({ files: [new File([new Blob([''])], 'probe.png', { type: 'image/png' })] });
    } catch {
      return false;
    }
  }, []);

  if (!isOpen) return null;

  // Get appropriate columns based on simulation mode (or use override)
  const shareColumns = columns || (simulationMode === 'custom' ? CUSTOM_SHARE_COLUMNS : PRESET_SHARE_COLUMNS);

  // Format the simulation settings header text
  const getSettingsText = () => {
    if (simulationMode === 'custom' && daysToTarget !== null && underlyingGain !== null) {
      const gainText = underlyingGain >= 0 ? `+${underlyingGain}%` : `${underlyingGain}%`;
      return `${daysToTarget} days  |  ${gainText} underlying`;
    }
    // For preset mode, show generic text since scenarios vary per row
    return 'Multiple Scenarios';
  };

  // Calculate target date from days to target
  const getTargetDate = (days) => {
    const today = new Date();
    const targetDate = new Date(today.getTime() + (days * 24 * 60 * 60 * 1000));
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format values for display
  const formatValue = (value, key) => {
    if (value === undefined || value === null || value === 'undefined') {
      return '-';
    }

    // Percentage columns
    if (key.includes('% Change') || key.includes('% Gain') || key === 'Underlying Gain %' || key === 'Underlying Scenario % Change') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
      }
    }

    // Currency columns
    if (key.includes('Premium') || key.includes('Underlying') || key === 'Strike' || key === 'Current' || key === 'Sim Price' || key === 'Sim Prem') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `$${num.toFixed(2)}`;
      }
    }

    return value;
  };

  // Get CSS class for value coloring
  const getValueClass = (value, key) => {
    if (key.includes('% Change') || key.includes('% Gain')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num > 0 ? 'share-value-positive' : num < 0 ? 'share-value-negative' : '';
      }
    }
    return '';
  };

  // Capture the content as an image.
  // Renders an off-screen clone at natural width so html2canvas can capture
  // the full table on mobile, where the live modal clips the wide table via
  // .share-table-wrapper { overflow-x: auto } and an mobile-width-bound parent.
  const captureImage = async () => {
    const original = captureRef.current;
    if (!original) return null;

    setIsCapturing(true);

    // Off-screen host that does NOT inherit the modal's width cap.
    // The user's visible modal is untouched; the clone renders invisibly.
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.left = '-99999px';
    host.style.top = '0';
    host.style.zIndex = '-1';
    host.style.background = '#0a0a0a';
    host.style.width = 'max-content';
    host.style.padding = '0';
    host.style.margin = '0';

    const clone = original.cloneNode(true);
    // Lift the inner overflow:auto on the clone so the table flows out to its
    // natural width — that's the column data that was getting clipped on mobile.
    clone.querySelectorAll('.share-table-wrapper').forEach((w) => {
      w.style.overflow = 'visible';
    });
    clone.style.width = 'max-content';

    host.appendChild(clone);
    document.body.appendChild(host);

    try {
      // One frame for cloned styles to apply and layout to settle.
      await new Promise((r) => requestAnimationFrame(r));

      const canvas = await html2canvas(clone, {
        backgroundColor: '#0a0a0a',
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
        // Force html2canvas's layout viewport to match the clone's natural width
        // so width-keyed media queries don't shrink content during render.
        windowWidth: Math.max(clone.scrollWidth, 1200),
      });
      return canvas;
    } catch (error) {
      console.error('Error capturing image:', error);
      return null;
    } finally {
      setIsCapturing(false);
      if (host.parentNode) host.parentNode.removeChild(host);
    }
  };

  // Download the image
  const handleDownload = async () => {
    const canvas = await captureImage();
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `watchlist-share-${new Date().toISOString().split('T')[0]}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Copy to clipboard
  const handleCopyToClipboard = async () => {
    const canvas = await captureImage();
    if (!canvas) {
      alert('Failed to capture image. Please try again.');
      return;
    }

    try {
      // Convert canvas to blob using a promise wrapper
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) {
            resolve(b);
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/png');
      });

      // Use the Clipboard API to write the image
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);

      // Show helpful error message
      alert(
        'Unable to copy image to clipboard.\n\n' +
        'This can happen if:\n' +
        '• Your browser doesn\'t support image clipboard\n' +
        '• The page isn\'t served over HTTPS\n\n' +
        'Try using "Download Image" instead.'
      );
    }
  };

  // Native OS share sheet (Web Share API)
  const handleShare = async () => {
    const canvas = await captureImage();
    if (!canvas) {
      alert('Failed to capture image. Please try again.');
      return;
    }

    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('blob failed'))), 'image/png');
      });

      const file = new File(
        [blob],
        `screener-share-${new Date().toISOString().split('T')[0]}.png`,
        { type: 'image/png' }
      );

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Options Screener Heatmap',
          text: 'optionsleveling.com',
        });
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      } else {
        alert('Sharing is not supported on this browser. Try "Download Image" or "Copy to Clipboard".');
      }
    } catch (error) {
      // AbortError = user cancelled the share sheet; not an error worth surfacing
      if (error && error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        alert('Unable to share. Try "Download Image" instead.');
      }
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="share-modal-header">
          <h2 className="share-modal-title">{title}</h2>
          <button onClick={onClose} className="share-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Area (scrollable) */}
        <div className="share-preview-wrapper">
          {/* Capture Container - This is what gets captured as an image */}
          <div ref={captureRef} className="share-capture-container">
            {/* Simulation Settings Header with Slider Visualization */}
            <div className="share-settings-header">
              <div className="share-settings-title">SIMULATION SETTINGS</div>

              {settingsHeader ? (
                settingsHeader
              ) : simulationMode === 'custom' && daysToTarget !== null && underlyingGain !== null ? (
                <div className="share-sliders-preview">
                  {/* Days to Exit Slider Visual */}
                  <div className="share-slider-row">
                    <span className="share-slider-label">Days to Exit</span>
                    <div className="share-slider-track">
                      <div
                        className="share-slider-fill"
                        style={{ width: `${Math.min((daysToTarget / 365) * 100, 100)}%` }}
                      />
                      <div
                        className="share-slider-thumb"
                        style={{ left: `${Math.min((daysToTarget / 365) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="share-slider-value">
                      <span className="share-slider-date">({getTargetDate(daysToTarget)})</span>
                      {daysToTarget} days
                    </span>
                  </div>

                  {/* Underlying Gain Slider Visual */}
                  <div className="share-slider-row">
                    <span className="share-slider-label">Underlying Gain</span>
                    <div className="share-slider-track">
                      <div
                        className="share-slider-fill"
                        style={{ width: `${Math.min(((underlyingGain + 90) / 290) * 100, 100)}%` }}
                      />
                      <div
                        className="share-slider-thumb"
                        style={{ left: `${Math.min(((underlyingGain + 90) / 290) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="share-slider-value">
                      {underlyingGain >= 0 ? '+' : ''}{underlyingGain}%
                    </span>
                  </div>
                </div>
              ) : (
                <div className="share-settings-value">{getSettingsText()}</div>
              )}
            </div>

            {/* Results Table */}
            <div className="share-table-wrapper">
              <table className="share-table">
                <thead>
                  {headerGroups && headerGroups.length > 0 && (
                    <tr>
                      {headerGroups.map((group, gIdx) => (
                        <th
                          key={gIdx}
                          colSpan={group.span}
                          className={`share-table-header-group ${group.className || ''}`}
                        >
                          {group.label}
                        </th>
                      ))}
                    </tr>
                  )}
                  <tr>
                    {shareColumns.map((col) => (
                      <th key={col.key} className="share-table-header">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 20).map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'share-row-even' : 'share-row-odd'}>
                      {shareColumns.map((col) => {
                        // Handle field name differences between modes
                        let value = row[col.key];

                        // Fallback for field name variations
                        if (value === undefined) {
                          if (col.key === 'Days to Gain') value = row['Days to Target'];
                          if (col.key === 'Days to Target') value = row['Days to Gain'];
                          if (col.key === 'Underlying Price') value = row['Current Underlying'];
                          if (col.key === 'Current Underlying') value = row['Underlying Price'];
                        }

                        const inlineStyle = cellStyleFn ? cellStyleFn(row, col) : null;
                        const hasInlineStyle = inlineStyle && Object.keys(inlineStyle).length > 0;
                        const valueClass = hasInlineStyle ? '' : getValueClass(value, col.key);
                        const content = col.render
                          ? col.render(row)
                          : formatValue(value, col.key);

                        return (
                          <td
                            key={col.key}
                            className={`share-table-cell ${valueClass} ${col.className || ''}`}
                            style={hasInlineStyle ? inlineStyle : undefined}
                          >
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.length > 20 && (
                <div className="share-table-truncated">
                  + {data.length - 20} more contracts
                </div>
              )}
            </div>

            {/* Branding Footer */}
            <div className="share-footer">
              <div className="share-footer-text">Customize your own scenarios at</div>
              <div className="share-footer-brand">optionsleveling.com</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="share-modal-actions">
          {canShareFiles && (
            <button
              onClick={handleShare}
              disabled={isCapturing}
              className="share-btn share-btn-primary"
            >
              {shareSuccess ? (
                <>
                  <Check className="w-4 h-4" />
                  Shared!
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" />
                  Share
                </>
              )}
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={isCapturing}
            className={canShareFiles ? 'share-btn share-btn-secondary' : 'share-btn share-btn-primary'}
          >
            <Download className="w-4 h-4" />
            {isCapturing ? 'Generating...' : 'Download Image'}
          </button>
          <button
            onClick={handleCopyToClipboard}
            disabled={isCapturing}
            className="share-btn share-btn-secondary"
          >
            {copySuccess ? (
              <>
                <Check className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy to Clipboard
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SharePreviewModal;
